import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { mensagemErro } from '../api/axios';
import { filtrosPorGuia, visaoPorGuia } from '../api/fluxfin';
import {
  Ajuda,
  Alerta,
  Badge,
  Botao,
  Busca,
  Indicador,
  LoadingSpinner,
  PageHeader,
  Paginacao,
  SelecaoBuscavel,
  Painel,
  Tabela,
  Th,
  Vazio,
} from '../components/common';
import DetalhesDaGuia from '../components/DetalhesDaGuia';
import { brl, formatDate, numero } from '../utils/formato';

/* Os filtros vivem na URL, como na auditoria de glosa: o recorte pode ser
   salvo, recarregado e mandado para outra pessoa sem precisar explicar como
   se chegou nele. */
const CAMPOS = [
  'competencia',
  'id_operadora',
  'id_arquivo',
  'protocolo',
  'situacao',
  'busca',
];

const TAMANHO = 50;

/* De onde veio o vínculo guia → protocolo. Um número digitado à mão entra na
   MESMA soma que um vindo do Bioslab, então a diferença precisa ser visível. */
const ORIGEM = {
  bioslab: null, // o caso normal não merece selo
  bioslab_guia_prestador: {
    tom: 'conferencia',
    texto: 'guia alternativa',
    ajuda:
      'O protocolo foi encontrado pela guia do prestador, não pela guia que a operadora imprimiu no arquivo.',
  },
  manual: {
    tom: 'pendente',
    texto: 'manual',
    ajuda: 'Vínculo informado por uma pessoa, não resolvido pelo Bioslab.',
  },
};

export default function Guias() {
  const [params, setParams] = useSearchParams();
  const [dados, setDados] = useState(null);
  const [opcoes, setOpcoes] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');
  const [guiaAberta, setGuiaAberta] = useState(null);

  // O texto digitado e o texto aplicado são coisas distintas: a consulta é
  // paginada no servidor, e disparar uma a cada tecla castigaria o banco e
  // faria a tela piscar resultados intermediários.
  const [texto, setTexto] = useState(params.get('busca') || '');

  const consulta = useMemo(() => {
    const q = {};
    CAMPOS.forEach((c) => {
      const v = params.get(c);
      if (v) q[c] = v;
    });
    q.pagina = Number(params.get('pagina') || 1);
    q.tamanho = TAMANHO;
    q.ordenar = params.get('ordenar') || 'valor_pago';
    q.asc = params.get('asc') === '1';
    return q;
  }, [params]);

  useEffect(() => {
    const t = setTimeout(() => {
      const atual = params.get('busca') || '';
      if (texto === atual) return;
      const novo = new URLSearchParams(params);
      if (texto) novo.set('busca', texto);
      else novo.delete('busca');
      novo.delete('pagina');
      setParams(novo, { replace: true });
    }, 400);
    return () => clearTimeout(t);
  }, [texto, params, setParams]);

  useEffect(() => {
    let vivo = true;
    setCarregando(true);
    Promise.all([visaoPorGuia(consulta), filtrosPorGuia(consulta)])
      .then(([r, f]) => {
        if (!vivo) return;
        setDados(r.data);
        setOpcoes(f.data);
        setErro('');
      })
      .catch((e) => {
        if (vivo) setErro(mensagemErro(e, 'Não foi possível carregar as guias.'));
      })
      .finally(() => vivo && setCarregando(false));
    return () => {
      vivo = false;
    };
  }, [consulta]);

  /* Trocar um filtro sempre volta para a página 1: manter a página seria
     mostrar "nenhum resultado" num recorte que tem resultados, só que na
     página 1. */
  const trocar = useCallback(
    (campo, valor) => {
      const novo = new URLSearchParams(params);
      if (valor) novo.set(campo, valor);
      else novo.delete(campo);
      novo.delete('pagina');
      setParams(novo, { replace: true });
    },
    [params, setParams]
  );

  const irPara = useCallback(
    (pagina) => {
      const novo = new URLSearchParams(params);
      if (pagina > 1) novo.set('pagina', String(pagina));
      else novo.delete('pagina');
      setParams(novo, { replace: true });
    },
    [params, setParams]
  );

  /* A ordenação é do SERVIDOR. O `Th` só precisa de um objeto com a mesma
     forma que o `useOrdenacao` devolve — então o componente é o mesmo das
     outras telas, sem saber que aqui quem ordena é o banco. */
  const ord = useMemo(
    () => ({
      campo: consulta.ordenar,
      asc: consulta.asc,
      ordenarPor: (campo) => {
        const novo = new URLSearchParams(params);
        const mesmo = consulta.ordenar === campo;
        novo.set('ordenar', campo);
        // Primeiro clique num campo novo: decrescente, que é o que interessa
        // numa tela de dinheiro. Clicar de novo inverte.
        if (mesmo && !consulta.asc) novo.set('asc', '1');
        else novo.delete('asc');
        novo.delete('pagina');
        setParams(novo, { replace: true });
      },
    }),
    [consulta, params, setParams]
  );

  const limpar = () => {
    setTexto('');
    setParams({}, { replace: true });
  };
  const algumFiltro = CAMPOS.some((c) => params.get(c));

  const r = dados?.resumo;
  const pg = dados?.paginacao;

  return (
    <>
      <PageHeader
        titulo="Por guia"
        subtitulo="O mesmo recorte da baixa, um nível abaixo: guia a guia, com o que entrou, o que foi recusado e de qual protocolo é."
      />

      <Alerta tipo="erro" onFechar={() => setErro('')}>
        {erro}
      </Alerta>

      {r && (
        <div className="ff-grid-tiles mb-3">
          <Indicador
            rotulo="Guias"
            valor={numero(r.qtd_guias)}
            nota="no recorte atual"
          />
          <Indicador
            rotulo="Recebido"
            valor={brl(r.total_pago)}
            natureza="entrada"
            nota="soma do valor pago por guia"
          />
          <Indicador
            rotulo="Glosado"
            valor={brl(r.total_glosado)}
            natureza={Number(r.total_glosado) > 0 ? 'atencao' : 'neutro'}
            nota="o que a operadora recusou"
          />
          <Indicador
            rotulo="Diferença"
            valor={brl(r.diferenca_falta)}
            natureza={Number(r.diferenca_falta) > 0 ? 'atencao' : 'neutro'}
            nota={
              /* Os dois sentidos NUNCA somados: no dado real eles quase se
                 anulam (+18.526 e −18.487), e o líquido de R$ 39 diria que
                 não há problema nenhum. */
              Number(r.diferenca_sobra) < 0
                ? `faltou · e ${brl(Math.abs(Number(r.diferenca_sobra)))} pagos a mais, em ${numero(r.qtd_diferenca)} guias`
                : `em ${numero(r.qtd_diferenca)} guia(s)`
            }
          />
          <Indicador
            rotulo="Sem protocolo"
            valor={numero(r.sem_protocolo)}
            natureza={r.sem_protocolo > 0 ? 'atencao' : 'neutro'}
            nota={
              r.sem_protocolo > 0
                ? 'dinheiro que entrou e ainda não se sabe de qual processo é'
                : 'toda guia do recorte resolveu'
            }
          />
        </div>
      )}

      <div className="mb-3">
        <Painel
          titulo="Filtros"
          acoes={
            (algumFiltro || texto) && (
              <Botao variante="secundario" pequeno onClick={limpar}>
                Limpar filtros
              </Botao>
            )
          }
        >
          {/* Em cascata, e cada um ignorando a si mesmo — a mesma convenção
              da auditoria de glosa e do saldo. */}
          <div className="p-3 d-flex flex-wrap gap-3 align-items-end">
            <SelecaoBuscavel
              rotulo="Competência"
              largura={150}
              textoTodas="Todas"
              valor={params.get('competencia') || ''}
              opcoes={(opcoes?.competencias || []).map((c) => ({ valor: c, texto: c }))}
              aoMudar={(v) => trocar('competencia', v)}
            />
            <SelecaoBuscavel
              rotulo="Operadora"
              largura={170}
              textoTodas="Todas"
              valor={params.get('id_operadora') || ''}
              opcoes={(opcoes?.operadoras || []).map((o) => ({
                valor: o.id_operadora,
                texto: o.nome,
              }))}
              aoMudar={(v) => trocar('id_operadora', v)}
            />
            <SelecaoBuscavel
              rotulo="Arquivo"
              largura={250}
              textoTodas="Todos"
              valor={params.get('id_arquivo') || ''}
              opcoes={(opcoes?.arquivos || []).map((a) => ({
                valor: a.id_arquivo,
                texto: a.nome,
              }))}
              aoMudar={(v) => trocar('id_arquivo', v)}
            />
            <SelecaoBuscavel
              rotulo="Protocolo"
              largura={200}
              textoTodas="Todos"
              valor={params.get('protocolo') || ''}
              opcoes={(opcoes?.protocolos || []).map((p) => ({ valor: p, texto: p }))}
              aoMudar={(v) => trocar('protocolo', v)}
            />
            <SelecaoBuscavel
              rotulo="Situação"
              largura={190}
              textoTodas="Todas"
              valor={params.get('situacao') || ''}
              opcoes={(opcoes?.situacoes || []).map((s) => ({
                valor: s.valor,
                texto: s.texto,
              }))}
              aoMudar={(v) => trocar('situacao', v)}
            />
            <div style={{ minWidth: 220, flex: '1 1 220px' }}>
              <label style={{ fontSize: 12, color: 'var(--ff-muted)' }}>
                Buscar guia
              </label>
              <Busca
                valor={texto}
                onChange={setTexto}
                placeholder="Número da guia"
                id="busca-guias"
              />
            </div>
          </div>
        </Painel>
      </div>

      <Painel
        titulo="Guia a guia"
        subtitulo={
          pg ? `${numero(pg.total)} guia(s) · ${numero(TAMANHO)} por página` : undefined
        }
        rodape={
          pg?.total > 0 && (
            <Paginacao
              pagina={pg.pagina}
              totalPaginas={Math.max(1, Math.ceil(pg.total / pg.tamanho))}
              total={pg.total}
              de={(pg.pagina - 1) * pg.tamanho + 1}
              ate={Math.min(pg.pagina * pg.tamanho, pg.total)}
              irPara={irPara}
              rotulo="guias"
            />
          )
        }
      >
        {carregando && !dados ? (
          <LoadingSpinner />
        ) : !dados?.linhas?.length ? (
          <Vazio
            icone="bi-list-ol"
            titulo="Nenhuma guia neste recorte"
            descricao={
              algumFiltro || texto
                ? 'Tente afrouxar os filtros.'
                : 'Lance um recebimento para que as guias apareçam aqui.'
            }
          />
        ) : (
          <div className="ff-tabela-wrap">
            <Tabela>
              <thead>
                <tr>
                  <Th campo="guia" estado={ord}>
                    Guia
                    <Ajuda titulo="Guia" origem="Arquivo da operadora">
                      O número impresso pela operadora no demonstrativo. Quando o
                      laboratório usa outro número, ele aparece logo abaixo como{' '}
                      <strong>alt.</strong> — e é por esse que o Bioslab é consultado,
                      porque o Bioslab é o sistema do laboratório.
                    </Ajuda>
                  </Th>
                  <Th campo="protocolo" estado={ord}>
                    Protocolo
                    <Ajuda titulo="Protocolo" origem="Fat_Apresentacao.NR_protocolo">
                      O processo a que esta guia pertence, resolvido no Bioslab. Vazio
                      significa que a guia não resolveu — o valor entrou, mas o sistema
                      ainda não sabe em qual processo lançá-lo.
                    </Ajuda>
                  </Th>
                  <Th campo="arquivo" estado={ord}>
                    Arquivo
                  </Th>
                  <Th campo="competencia" estado={ord}>
                    Competência
                    <Ajuda titulo="Competência" origem="Cabeçalho do demonstrativo">
                      Mês em que a operadora efetuou o pagamento — não é o mês do
                      atendimento.
                    </Ajuda>
                  </Th>
                  <Th campo="qtd_linhas" estado={ord} alinhar="right">
                    Itens
                  </Th>
                  <Th campo="valor_cobrado" estado={ord} alinhar="right">
                    Cobrado
                    <Ajuda
                      titulo="Valor cobrado"
                      origem="Exame_atendimento.vlr_contratante"
                    >
                      Quanto o Bioslab registra que foi cobrado nesta guia. Vazio
                      quando a guia não resolveu protocolo: não há com o que comparar.
                    </Ajuda>
                  </Th>
                  <Th campo="valor_pago" estado={ord} alinhar="right">
                    Pago
                  </Th>
                  <Th campo="valor_glosado" estado={ord} alinhar="right">
                    Glosado
                    <Ajuda titulo="Valor glosado" origem="Arquivo da operadora">
                      Célula vazia <strong>não é zero</strong>: significa que nenhum
                      arquivo falou de glosa para esta guia ainda — na Unimed, porque o
                      extrato vem num segundo documento que pode não ter chegado.
                    </Ajuda>
                  </Th>
                  <Th campo="diferenca" estado={ord} alinhar="right">
                    Diferença
                    <Ajuda
                      titulo="Não explicado"
                      origem="cobrado \u2212 pago \u2212 glosado"
                    >
                      O que sobrou <strong>sem explicação</strong>. Positivo é
                      dinheiro que faltou; negativo é pagamento acima do cobrado.
                      <br />
                      <br />
                      A conta anterior era{' '}
                      <span className="ff-mono">pago − cobrado</span> e respondia a
                      pergunta errada: uma guia cobrada a R$ 1.720,05, paga a
                      R$ 1.649,05 e glosada em R$ 71,00 aparecia com R$ 71,00 de
                      divergência quando <strong>não há divergência nenhuma</strong>
                      — as duas pontas somam o que foi cobrado.
                      <br />
                      <br />
                      Célula vazia significa que <strong>não dá para calcular</strong>:
                      falta o cobrado do Bioslab, ou a glosa ainda não chegou. Tratar
                      glosa desconhecida como zero criaria um problema inventado.
                    </Ajuda>
                  </Th>
                  <Th>Atendimento</Th>
                  <Th alinhar="right">{''}</Th>
                </tr>
              </thead>
              <tbody>
                {dados.linhas.map((l) => {
                  const selo = ORIGEM[l.origem];
                  return (
                    <tr key={`${l.guia}|${l.id_arquivo}`}>
                      <td className="col-codigo">
                        {l.guia}
                        {l.guia_alternativa && l.guia_alternativa !== l.guia && (
                          <div
                            style={{ fontSize: 12, color: 'var(--ff-muted)' }}
                            title="Guia do prestador — é por este número que o Bioslab é consultado."
                          >
                            alt. {l.guia_alternativa}
                          </div>
                        )}
                      </td>
                      <td className="col-codigo">
                        {l.protocolo || (
                          <Badge tom="pendente">sem protocolo</Badge>
                        )}
                        {selo && (
                          <div className="mt-1" title={selo.ajuda}>
                            <Badge tom={selo.tom}>{selo.texto}</Badge>
                          </div>
                        )}
                      </td>
                      <td className="col-codigo">
                        {l.arquivo}
                        <div style={{ fontSize: 12, color: 'var(--ff-muted)' }}>
                          {l.operadora}
                        </div>
                      </td>
                      <td className="col-meta">{l.competencia || '—'}</td>
                      <td className="col-qtd">
                        {numero(l.qtd_linhas)}
                        {l.confere === false && (
                          <div
                            style={{ fontSize: 12, color: 'var(--ff-error-ink)' }}
                            title="A soma dos itens não bate com o subtotal impresso para esta guia."
                          >
                            não fecha
                          </div>
                        )}
                      </td>
                      <td className="col-valor">
                        {l.valor_cobrado === null ? (
                          <span style={{ color: 'var(--ff-border-field)' }}>—</span>
                        ) : (
                          brl(l.valor_cobrado)
                        )}
                      </td>
                      <td className="col-valor">{brl(l.valor_pago)}</td>
                      <td className="col-valor">
                        {/* Vazio não é zero — ver a ajuda no cabeçalho. */}
                        {l.valor_glosado === null ? (
                          <span style={{ color: 'var(--ff-border-field)' }}>—</span>
                        ) : Number(l.valor_glosado) > 0 ? (
                          <span style={{ color: 'var(--ff-error-ink)' }}>
                            {brl(l.valor_glosado)}
                          </span>
                        ) : (
                          brl(l.valor_glosado)
                        )}
                      </td>
                      <td className="col-valor">
                        {/* Vazio = nao da para calcular, nao zero. */}
                        {l.diferenca === null ? (
                          <span
                            style={{ color: 'var(--ff-border-field)' }}
                            title="Falta o cobrado do Bioslab ou a glosa ainda não chegou."
                          >
                            —
                          </span>
                        ) : Number(l.diferenca) === 0 ? (
                          <span style={{ color: 'var(--ff-border-field)' }}>
                            {brl(0)}
                          </span>
                        ) : (
                          <span
                            style={{
                              color:
                                Number(l.diferenca) > 0
                                  ? 'var(--ff-error-ink)'
                                  : 'var(--ff-muted)',
                            }}
                          >
                            {brl(l.diferenca)}
                          </span>
                        )}
                      </td>
                      <td className="col-meta">
                        {l.atendimento_de
                          ? formatDate(l.atendimento_de) +
                            (l.atendimento_ate && l.atendimento_ate !== l.atendimento_de
                              ? ' – ' + formatDate(l.atendimento_ate)
                              : '')
                          : '—'}
                      </td>
                      <td className="col-valor">
                        <Botao
                          variante="secundario"
                          pequeno
                          icone="bi-list-ul"
                          onClick={() => setGuiaAberta(l)}
                        >
                          Itens
                        </Botao>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Tabela>
          </div>
        )}
      </Painel>

      {guiaAberta && (
        <DetalhesDaGuia guia={guiaAberta.guia} aoFechar={() => setGuiaAberta(null)} />
      )}
    </>
  );
}
